import { useState } from "react";
import { SchemaFlujoEntidad, type SchemaFlujoEntidadIn } from "./FormsFlujo/Flujo.schema";
import { DiagramaFlujo } from "./FlujoDiagrama";
import { FormProyectoProceso } from "./FormsFlujo/Proceso.form";
import { FormProyectoEntrada } from "./FormsFlujo/Entrada.form";
import { FormProyectoSalida } from "./FormsFlujo/Salida.form";
import { useEditableStore, type ItemData, type EditableStoreApi } from "@/hooks/useMyFormApi";
import { Button } from "@/components/ui/button";
import type { ApiProyectoFull } from "@/@types/Api_Extracted";
import { useApi } from "@/hooks/useApi";

export function FlujoForm(props: { readonly proyecto: ApiProyectoFull; readonly idProyecto: string; }) {
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const entidades = parseProyecto(props.proyecto);
	const formApi = useEditableStore(entidades, SchemaFlujoEntidad);
	const api = useApi();
	const mutation = api.useMutation("put", "/v1/Empresa/Proyectos/{idProyecto}", {
		onSuccess: () => {
			alert("Proyecto guardado correctamente.");
		},
	});

	const handleGuardar = () => {
		const result = formApi.parse();
		if (result.error) {
			alert("Hay errores de validación. Por favor, corríjalos antes de guardar.");
			return;
		}
		const proceso = result.data.find((e) => e.tipo == "proceso");
		if (!proceso) {
			alert("No se encontró el proceso principal. Contacte con el administrador.");
			return;
		}

		mutation.mutate({
			params: { path: { idProyecto: props.idProyecto, } },
			body: {
				proceso,
				entradas: result.data.filter((e) => e.tipo == "entrada"),
				salidas: result.data.filter((e) => e.tipo == "salida"),
			},
		});
	};

	return (
		<div className="flex flex-1 gap-4">
			<div className="w-4/5 flex flex-col" >
				<DiagramaFlujo
					entities={formApi.values}
					erroring={formApi.erroringIds}
					selectedId={selectedId}
					setSelectedId={setSelectedId}
				/>
				<Button variant="primary" className="w-full mt-10" onClick={handleGuardar}>
					Guardar cambios
				</Button>

			</div>
			<div className="border rounded p-4 w-1/5">
				<FormEntity
					formApi={formApi}
					selectedId={selectedId}
					referenciaProyecto={props.proyecto.proceso.referencia}
				/>
			</div>
		</div>
	);
}

function parseProyecto(proyecto: ApiProyectoFull): SchemaFlujoEntidad[] {
	const entities: SchemaFlujoEntidad[] = [];

	// Agregar el proceso principal
	entities.push(proyecto.proceso);
	entities.push(...proyecto.entradas);
	entities.push(...proyecto.salidas);

	return entities;
}


interface FormEntityProps {
	readonly formApi: EditableStoreApi<typeof SchemaFlujoEntidad>;
	readonly selectedId: string | null;
	readonly referenciaProyecto: string;
}
function FormEntity(props: FormEntityProps) {
	const { selectedId, formApi } = props;
	const selectedEntity = selectedId ? formApi.itemData(selectedId) : null;

	if (selectedEntity == null) {
		return <p>Seleccione un elemento para editar</p>;
	}

	const handleDelete = (node: SchemaFlujoEntidadIn) => {
		if (node.tipo == "proceso") {
			alert("No se puede eliminar el proceso principal.");
			return;
		}

		function getDescendants(idProceso: string) {
			const subprocesos = [
				...formApi.values.filter((e) => e.tipo == "entrada" && e.idProcesoTo == idProceso),
				...formApi.values.filter((e) => e.tipo == "salida" && e.idProcesoFrom == idProceso),
			];
			const ids = new Array<string>();
			subprocesos.forEach((subproceso) => {
				ids.push(subproceso.id);

				if (subproceso.tipo == "entrada" && subproceso.subtipo == "sub_proceso") {
					ids.push(...getDescendants(subproceso.id));
				}
			});
			return ids;
		}

		if (node.tipo == "entrada") {
			const siblings = formApi.values.filter((e) => e.tipo == node.tipo && e.idProcesoTo == node.idProcesoTo);
			if (siblings.length == 1) {	//Solo quedo yo
				const parent = formApi.values.find((e) => e.id == (node.idProcesoTo ?? node.idProyecto));
				alert(`No se puede eliminar la última ${node.tipo} de ${parent?.nombre}.`);
				return;
			}

			const descendants = getDescendants(node.id);
			descendants.forEach((id) => formApi.setRemoved(id));
		}

		formApi.setRemoved(node.id);
	};

	// En un mundo ideal podria introducir un guard de que dirtyEntity.tipo == selectedEntity.tipo
	// Pero en la version actual typescript no "mantiene" ese narrow dentro del switch. Usamos "as" para forzarlo, no deberia poder cambiarse.
	switch (selectedEntity.value.tipo) {
		case "proceso": {
			const item = selectedEntity as ItemData<SchemaFlujoEntidadIn & { tipo: "proceso"; }>;
			return <FormProyectoProceso
				value={item.value}
				onChange={formApi.setDirty}
				onAdd={formApi.setDirty}
				errors={item.errors}
			/>;
		}
		case "entrada": {
			const item = selectedEntity as ItemData<SchemaFlujoEntidadIn & { tipo: "entrada"; }>;
			return <FormProyectoEntrada
				value={item.value}
				onChange={formApi.setDirty}
				onAdd={formApi.setDirty}
				onDelete={handleDelete}
				errors={item.errors}
				referenciaProyecto={props.referenciaProyecto}
			/>;
		}
		case "salida": {
			const item = selectedEntity as ItemData<SchemaFlujoEntidadIn & { tipo: "salida"; }>;
			return <FormProyectoSalida
				value={item.value}
				onChange={formApi.setDirty}
				onDelete={handleDelete}
				errors={item.errors}
			/>;
		}
		default:
			return <p>Tipo de flujo desconocido.</p>;
	}
}
